import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useMediaQuery,
  useTheme,
  LinearProgress,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
  Settings as SettingsIcon,
  AutoAwesome as AutoAwesomeIcon,
  KeyboardArrowUp as ArrowUpIcon,
  KeyboardArrowDown as ArrowDownIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { trailsService } from '../services/trailsService';
import { devotionalService } from '../services/devotionalService';
import { integrationsService } from '../services/integrationsService';
import { Trail, Devotional } from '../types';

// Converte "mm:ss" ou "hh:mm:ss" para segundos
const parseDuration = (value: string): number => {
  if (!value) return 0;
  if (/^\d+$/.test(value)) return parseInt(value);

  const parts = value.split(':').map(p => parseInt(p) || 0);
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];

  return parseInt(value) || 0;
};

// Converte segundos para "mm:ss" ou "hh:mm:ss"
const formatDuration = (seconds: number): string => {
  if (!seconds) return '';

  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;

  if (h > 0) {
    return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const TrailDevotionals: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [trail, setTrail] = useState<Trail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedDevotional, setSelectedDevotional] = useState<Devotional | null>(null);
  const [processingVideo, setProcessingVideo] = useState(false);
  const [processingStep, setProcessingStep] = useState('');

  const [devotionalForm, setDevotionalForm] = useState({
    dayNumber: 1,
    title: '',
    description: '',
    bibleReference: '',
    verseText: '',
    mediaUrl: '',
    mediaType: 'video' as 'video' | 'audio',
    thumbnailUrl: '',
    duration: '',
  });

  const fetchTrail = useCallback(async () => {
    try {
      const data = await trailsService.getOne(id!);
      // Ordena devocionais por dayNumber
      if (data.devotionals) {
        data.devotionals.sort((a, b) => (a.dayNumber || 0) - (b.dayNumber || 0));
      }
      setTrail(data);
    } catch (err: any) {
      setError('Erro ao carregar trilha');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchTrail();
    }
  }, [id, fetchTrail]);

  const handleOpenDevotionalDialog = (devotional?: Devotional) => {
    if (devotional) {
      setSelectedDevotional(devotional);
      setDevotionalForm({
        dayNumber: devotional.dayNumber || 1,
        title: devotional.title,
        description: devotional.content || devotional.description || '',
        bibleReference: devotional.verse || devotional.bibleReference || '',
        verseText: devotional.verseText || '',
        mediaUrl: devotional.videoUrl || '',
        mediaType: 'video',
        thumbnailUrl: devotional.thumbnail || devotional.thumbnailUrl || '',
        duration: formatDuration(devotional.duration || 0),
      });
    } else {
      setSelectedDevotional(null);
      const nextDay = trail?.devotionals?.length ? trail.devotionals.length + 1 : 1;
      setDevotionalForm({
        dayNumber: nextDay,
        title: '',
        description: '',
        bibleReference: '',
        verseText: '',
        mediaUrl: '',
        mediaType: 'video',
        thumbnailUrl: '',
        duration: '',
      });
    }
    setOpenDialog(true);
  };

  const handleSaveDevotional = async () => {
    if (!devotionalForm.title || !devotionalForm.mediaUrl) {
      setError('Preencha todos os campos obrigatórios do devocional');
      return;
    }

    setSaving(true);
    try {
      const devotionalData = {
        trailId: id,
        dayNumber: devotionalForm.dayNumber,
        title: devotionalForm.title,
        description: devotionalForm.description,
        bibleReference: devotionalForm.bibleReference,
        verseText: devotionalForm.verseText,
        videoUrl: devotionalForm.mediaUrl,
        thumbnailUrl: devotionalForm.thumbnailUrl,
        duration: parseDuration(devotionalForm.duration),
        isPublished: true,
      };

      if (selectedDevotional) {
        await devotionalService.update(selectedDevotional.id, devotionalData);
      } else {
        await devotionalService.create(devotionalData);
      }

      setOpenDialog(false);
      fetchTrail();
      setError('');
    } catch (err: any) {
      setError('Erro ao salvar devocional');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDevotional = async (devotionalId: string) => {
    if (window.confirm('Tem certeza que deseja excluir este devocional?')) {
      try {
        await devotionalService.delete(devotionalId);
        fetchTrail();
      } catch (err) {
        setError('Erro ao excluir devocional');
      }
    }
  };

  // Move devocional para cima ou para baixo
  const handleMoveDevotional = async (index: number, direction: 'up' | 'down') => {
    if (!trail?.devotionals) return;

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= trail.devotionals.length) return;

    const currentItem = trail.devotionals[index];
    const targetItem = trail.devotionals[newIndex];

    // Usa índices +1 como base para garantir valores únicos
    const currentNewDay = newIndex + 1;
    const targetNewDay = index + 1;

    try {
      await Promise.all([
        devotionalService.update(currentItem.id, { dayNumber: currentNewDay }),
        devotionalService.update(targetItem.id, { dayNumber: targetNewDay }),
      ]);
      fetchTrail();
    } catch (err) {
      setError('Erro ao reordenar devocional');
    }
  };

  // Processa o vídeo do YouTube e preenche todos os campos automaticamente
  const handleProcessVideo = async (url: string) => {
    setDevotionalForm(prev => ({ ...prev, mediaUrl: url }));

    // Verifica se é uma URL do YouTube válida
    const youtubeRegex = /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([^&\n?#]+)/;
    if (!youtubeRegex.test(url) && !/^[a-zA-Z0-9_-]{11}$/.test(url)) {
      return;
    }

    setProcessingVideo(true);
    setError('');

    try {
      setProcessingStep('Buscando informações do vídeo...');
      const videoInfo = await integrationsService.processVideo(url);

      setDevotionalForm(prev => ({
        ...prev,
        mediaUrl: videoInfo.embedUrl || `https://www.youtube.com/embed/${videoInfo.videoId}`,
        title: videoInfo.title,
        thumbnailUrl: videoInfo.thumbnailUrl,
        duration: formatDuration(videoInfo.duration),
      }));

    } catch (err: any) {
      console.error('Erro ao processar vídeo:', err);
      setError('Erro ao processar vídeo. Verifique se a URL está correta.');
    } finally {
      setProcessingVideo(false);
      setProcessingStep('');
    }
  };

  // Gera reflexão com Gemini baseado no texto do versículo
  const handleGenerateReflection = async () => {
    const text = devotionalForm.verseText?.trim();
    if (!text || text.length < 50) {
      return; // Precisa de pelo menos 50 caracteres
    }

    // Não gera se já tem reflexão preenchida
    if (devotionalForm.description?.trim()) {
      return;
    }

    setProcessingVideo(true);
    setProcessingStep('Gerando reflexão com IA...');

    try {
      const reflection = await integrationsService.generateReflection(
        text,
        devotionalForm.bibleReference || devotionalForm.title
      );
      setDevotionalForm(prev => ({
        ...prev,
        description: reflection,
      }));
    } catch (err) {
      console.error('Erro ao gerar reflexão:', err);
    } finally {
      setProcessingVideo(false);
      setProcessingStep('');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!trail) {
    return (
      <Box>
        <Alert severity="error">Trilha não encontrada</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: isMobile ? 2 : 0 }}>
      {/* Header */}
      <Box sx={{
        mb: 3,
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'flex-start' : 'center',
        gap: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
          <IconButton onClick={() => navigate('/devotionals')} size={isMobile ? 'small' : 'medium'}>
            <BackIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              variant={isMobile ? 'h6' : 'h4'}
              component="h1"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {trail.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {trail.devotionals?.length || 0} devocionais
            </Typography>
          </Box>
        </Box>

        <Box sx={{
          display: 'flex',
          gap: 1,
          width: isMobile ? '100%' : 'auto',
          flexShrink: 0,
        }}>
          <Button
            variant="outlined"
            startIcon={!isMobile && <SettingsIcon />}
            onClick={() => navigate(`/trails/${id}/edit`)}
            size={isMobile ? 'small' : 'medium'}
            sx={{
              flex: isMobile ? 1 : 'none',
              borderColor: '#4CAF50',
              color: '#4CAF50',
              '&:hover': {
                borderColor: '#43A047',
                background: 'rgba(76, 175, 80, 0.04)',
              },
            }}
          >
            {isMobile ? <SettingsIcon /> : 'Editar Trilha'}
          </Button>
          <Button
            variant="contained"
            startIcon={!isMobile && <AddIcon />}
            onClick={() => handleOpenDevotionalDialog()}
            size={isMobile ? 'small' : 'medium'}
            sx={{
              flex: isMobile ? 1 : 'none',
              background: 'linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)',
              boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #43A047 0%, #5CB860 100%)',
              },
            }}
          >
            {isMobile ? <><AddIcon sx={{ mr: 0.5 }} /> Adicionar</> : 'Adicionar Devocional'}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Lista de Devocionais */}
      {trail.devotionals && trail.devotionals.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 1.5 : 2 }}>
          {trail.devotionals.map((devotional, index) => (
            <Card
              key={devotional.id}
              sx={{
                transition: 'all 0.2s',
                overflow: 'hidden',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                },
              }}
            >
              {isMobile ? (
                // Layout Mobile - Thumbnail em cima
                <Box>
                  {/* Thumbnail com overlay */}
                  <Box sx={{ position: 'relative' }}>
                    {devotional.thumbnail || devotional.thumbnailUrl ? (
                      <Box
                        component="img"
                        src={devotional.thumbnail || devotional.thumbnailUrl}
                        alt={devotional.title}
                        sx={{
                          width: '100%',
                          height: 180,
                          objectFit: 'cover',
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: '100%',
                          height: 180,
                          backgroundColor: '#E8F5E9',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <PlayIcon sx={{ color: '#4CAF50', fontSize: 48 }} />
                      </Box>
                    )}
                    {/* Overlay com dia e duração */}
                    <Box sx={{
                      position: 'absolute',
                      top: 8,
                      left: 8,
                      right: 8,
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}>
                      <Chip
                        label={`Dia ${devotional.dayNumber || index + 1}`}
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(76, 175, 80, 0.9)',
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                        }}
                      />
                      {devotional.duration && (
                        <Chip
                          label={`${Math.floor(devotional.duration / 60)}:${(devotional.duration % 60).toString().padStart(2, '0')}`}
                          size="small"
                          sx={{
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                            color: 'white',
                            fontSize: '0.75rem',
                          }}
                        />
                      )}
                    </Box>
                    {/* Play button overlay */}
                    <IconButton
                      onClick={() => window.open(devotional.videoUrl, '_blank')}
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        '&:hover': {
                          backgroundColor: 'white',
                        },
                      }}
                    >
                      <PlayIcon sx={{ color: '#4CAF50', fontSize: 32 }} />
                    </IconButton>
                  </Box>
                  {/* Conteúdo */}
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        lineHeight: 1.3,
                        mb: 1,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {devotional.title}
                    </Typography>
                    {/* Ações */}
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      {/* Botões de reordenar */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', mr: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleMoveDevotional(index, 'up')}
                          disabled={index === 0}
                          sx={{ p: 0.5 }}
                        >
                          <ArrowUpIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleMoveDevotional(index, 'down')}
                          disabled={index === (trail.devotionals?.length || 0) - 1}
                          sx={{ p: 0.5 }}
                        >
                          <ArrowDownIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={() => handleOpenDevotionalDialog(devotional)}
                        sx={{
                          flex: 1,
                          borderColor: '#4CAF50',
                          color: '#4CAF50',
                        }}
                      >
                        Editar
                      </Button>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteDevotional(devotional.id)}
                        sx={{ border: '1px solid', borderColor: 'error.main' }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Box>
              ) : (
                // Layout Desktop - Horizontal
                <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    {/* Thumbnail */}
                    <Box sx={{ position: 'relative', flexShrink: 0 }}>
                      {devotional.thumbnail || devotional.thumbnailUrl ? (
                        <Box
                          component="img"
                          src={devotional.thumbnail || devotional.thumbnailUrl}
                          alt={devotional.title}
                          sx={{
                            width: 160,
                            height: 90,
                            objectFit: 'cover',
                            borderRadius: 1,
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: 160,
                            height: 90,
                            backgroundColor: '#E8F5E9',
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <PlayIcon sx={{ color: '#4CAF50', fontSize: 32 }} />
                        </Box>
                      )}
                      {devotional.duration && (
                        <Chip
                          label={`${Math.floor(devotional.duration / 60)}:${(devotional.duration % 60).toString().padStart(2, '0')}`}
                          size="small"
                          sx={{
                            position: 'absolute',
                            bottom: 4,
                            right: 4,
                            backgroundColor: 'rgba(0, 0, 0, 0.8)',
                            color: 'white',
                            fontSize: '0.7rem',
                            height: 20,
                          }}
                        />
                      )}
                    </Box>

                    {/* Content */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Chip
                          label={`Dia ${devotional.dayNumber || index + 1}`}
                          size="small"
                          color="primary"
                          sx={{ height: 22, fontSize: '0.75rem' }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {devotional.viewCount?.toLocaleString() || 0} visualizações
                        </Typography>
                      </Box>

                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          lineHeight: 1.3,
                        }}
                      >
                        {devotional.title}
                      </Typography>

                      {(devotional.verse || devotional.bibleReference) && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {devotional.verse || devotional.bibleReference}
                        </Typography>
                      )}
                    </Box>

                    {/* Actions */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                      {/* Botões de reordenar */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', mr: 0.5 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleMoveDevotional(index, 'up')}
                          disabled={index === 0}
                          title="Mover para cima"
                          sx={{ p: 0.25 }}
                        >
                          <ArrowUpIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleMoveDevotional(index, 'down')}
                          disabled={index === (trail.devotionals?.length || 0) - 1}
                          title="Mover para baixo"
                          sx={{ p: 0.25 }}
                        >
                          <ArrowDownIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => window.open(devotional.videoUrl, '_blank')}
                        title="Reproduzir"
                        sx={{ color: '#4CAF50' }}
                      >
                        <PlayIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDevotionalDialog(devotional)}
                        title="Editar"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteDevotional(devotional.id)}
                        title="Excluir"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </CardContent>
              )}
            </Card>
          ))}
        </Box>
      ) : (
        <Card>
          <CardContent>
            <Box
              sx={{
                textAlign: 'center',
                py: 6,
                background: '#F5F7FA',
                borderRadius: 2,
              }}
            >
              <Typography variant="body1" color="text.secondary" gutterBottom>
                Nenhum devocional nesta trilha ainda
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Comece adicionando o primeiro devocional
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDevotionalDialog()}
                sx={{
                  background: 'linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)',
                }}
              >
                Adicionar Primeiro Devocional
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => !processingVideo && setOpenDialog(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {selectedDevotional ? 'Editar Devocional' : 'Adicionar Devocional'}
            {processingVideo && (
              <Chip
                icon={<AutoAwesomeIcon />}
                label={processingStep}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
        </DialogTitle>

        {processingVideo && <LinearProgress />}

        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            {/* Link do YouTube - PRIMEIRO CAMPO */}
            <TextField
              fullWidth
              label="Link do YouTube"
              value={devotionalForm.mediaUrl}
              onChange={(e) => setDevotionalForm({ ...devotionalForm, mediaUrl: e.target.value })}
              onBlur={(e) => handleProcessVideo(e.target.value)}
              required
              InputLabelProps={{ shrink: true }}
              placeholder="https://youtube.com/watch?v=..."
              helperText="Cole o link e os campos serão preenchidos automaticamente"
              size={isMobile ? 'small' : 'medium'}
              disabled={processingVideo}
              InputProps={{
                endAdornment: processingVideo ? <CircularProgress size={20} /> : null,
              }}
            />

            {/* Título */}
            <TextField
              fullWidth
              label="Título"
              value={devotionalForm.title}
              onChange={(e) => setDevotionalForm({ ...devotionalForm, title: e.target.value })}
              required
              InputLabelProps={{ shrink: true }}
              placeholder="Ex: Pequenos Passos, Grande Fé"
              size={isMobile ? 'small' : 'medium'}
              disabled={processingVideo}
            />

            {/* Texto do Versículo (descrição/transcrição do vídeo) */}
            <TextField
              fullWidth
              label="Texto do Versículo / Transcrição"
              value={devotionalForm.verseText}
              onChange={(e) => setDevotionalForm({ ...devotionalForm, verseText: e.target.value })}
              onBlur={handleGenerateReflection}
              multiline
              rows={3}
              InputLabelProps={{ shrink: true }}
              placeholder="Cole a transcrição do vídeo aqui..."
              helperText="Ao sair do campo, a reflexão será gerada automaticamente"
              size={isMobile ? 'small' : 'medium'}
              disabled={processingVideo}
            />

            {/* Descrição / Reflexão (gerada pelo Gemini) */}
            <TextField
              fullWidth
              label="Reflexão"
              value={devotionalForm.description}
              onChange={(e) => setDevotionalForm({ ...devotionalForm, description: e.target.value })}
              multiline
              rows={isMobile ? 4 : 5}
              InputLabelProps={{ shrink: true }}
              placeholder="Reflexão gerada automaticamente pela IA..."
              size={isMobile ? 'small' : 'medium'}
              disabled={processingVideo}
              helperText="Gerada automaticamente pelo Gemini"
            />

            {/* Tipo e Duração */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <FormControl fullWidth size={isMobile ? 'small' : 'medium'} disabled={processingVideo}>
                <InputLabel shrink id="media-type-label">
                  Tipo
                </InputLabel>
                <Select
                  labelId="media-type-label"
                  value={devotionalForm.mediaType}
                  onChange={(e) => setDevotionalForm({ ...devotionalForm, mediaType: e.target.value as 'video' | 'audio' })}
                  label="Tipo"
                  notched
                >
                  <MenuItem value="video">Vídeo</MenuItem>
                  <MenuItem value="audio">Áudio</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Duração"
                value={devotionalForm.duration}
                onChange={(e) => setDevotionalForm({ ...devotionalForm, duration: e.target.value })}
                InputLabelProps={{ shrink: true }}
                placeholder="5:30"
                helperText="mm:ss"
                size={isMobile ? 'small' : 'medium'}
                disabled={processingVideo}
              />
            </Box>

            {/* Thumbnail */}
            <TextField
              fullWidth
              label="URL da Thumbnail"
              value={devotionalForm.thumbnailUrl}
              onChange={(e) => setDevotionalForm({ ...devotionalForm, thumbnailUrl: e.target.value })}
              InputLabelProps={{ shrink: true }}
              placeholder="https://exemplo.com/thumbnail.jpg"
              size={isMobile ? 'small' : 'medium'}
              disabled={processingVideo}
            />

            {/* Preview da Thumbnail */}
            {devotionalForm.thumbnailUrl && (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Box
                  component="img"
                  src={devotionalForm.thumbnailUrl}
                  alt="Thumbnail preview"
                  sx={{
                    maxWidth: '100%',
                    maxHeight: 200,
                    borderRadius: 1,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
          <Button
            onClick={() => setOpenDialog(false)}
            size={isMobile ? 'small' : 'medium'}
            disabled={processingVideo}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSaveDevotional}
            variant="contained"
            disabled={saving || processingVideo}
            size={isMobile ? 'small' : 'medium'}
            sx={{
              background: 'linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)',
            }}
          >
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TrailDevotionals;
